import { RefObject, useCallback, useEffect, useState } from 'react'
import { useScroll, useWindowSize } from 'react-use'

import { canScrollRight } from '@/lib/helpers/domHelpers'

export function useCanScroll(elementRef: RefObject<HTMLElement | null>) {
  const { x: scrollLeft } = useScroll(elementRef as RefObject<HTMLElement>)
  const [element, setElement] = useState(elementRef.current)
  const { width: windowWidth } = useWindowSize()
  const canScrollToTheRight = !!element && canScrollRight(element)

  const getCanScrollValue = useCallback(() => {
    return { left: scrollLeft > 0, right: canScrollToTheRight }
  }, [canScrollToTheRight, scrollLeft])

  const [canScroll, setCanScroll] = useState(getCanScrollValue)

  /**
   * When the elementRef is set on initial mount, update the `element` state to
   * trigger a re-render of the component using this hook. React doesn't
   * re-render component if its ref changes so updating the state like this
   * ensures we re-run this hook when the ref is first set.
   */
  useEffect(() => {
    setElement(elementRef.current)
  }, [elementRef])

  useEffect(() => {
    setCanScroll(getCanScrollValue())
  }, [getCanScrollValue, windowWidth])

  return canScroll
}
